import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { useAuth } from "../lib/auth";
import { formatError } from "../lib/api";
import { GoogleLogo } from "@phosphor-icons/react";
import Seo from "../components/Seo";
import LoginTransition from "../components/LoginTransition";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogle() {
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [transition, setTransition] = useState(null); // { user, target }

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await login(email, password);
      // Play the luxury command-center loading transition before landing.
      const target = u && !u.username_set ? "/setup-username" : "/app";
      setTransition({ user: u, target });
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <><Seo title="Sign in" description="Sign in to HigherVision — Hawaii's social network for jobs, teams, events, and community." />
    <AnimatePresence>
      {transition && (
        <LoginTransition
          userName={transition.user?.name || transition.user?.username}
          onComplete={() => nav(transition.target, { replace: true })}
        />
      )}
    </AnimatePresence>
    <div className="min-h-screen bg-background grid place-items-center px-6 py-10 relative">
      <div className="absolute inset-0 grid-bg opacity-50" />
      <div className="relative w-full max-w-md card-sharp p-8 fade-in">
        <Link to="/" className="font-display text-xl tracking-tight block mb-6">
          <span className="text-neon-pink">HIGHER</span><span className="text-neon-cyan">VISION</span>
        </Link>
        <h1 className="font-display text-3xl">Sign in.</h1>
        <p className="text-sm text-muted-foreground mt-1">Welcome back.</p>

        <button
          data-testid="google-login-btn"
          onClick={startGoogle}
          className="btn-neon w-full justify-center mt-6 glow-pink"
        >
          <GoogleLogo size={16} weight="bold" /> Continue with Google
        </button>

        <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-widest text-muted-foreground">
          <div className="flex-1 h-px bg-white/10" /> or <div className="flex-1 h-px bg-white/10" />
        </div>

        <form onSubmit={submit} className="space-y-3">
          <label className="block text-xs uppercase tracking-widest text-muted-foreground">Email</label>
          <input
            data-testid="login-email"
            className="input-sharp" type="email" required
            value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
          <label className="block text-xs uppercase tracking-widest text-muted-foreground">Password</label>
          <input
            data-testid="login-password"
            className="input-sharp" type="password" required
            value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
          />
          {err && <div data-testid="login-error" className="text-sm text-[#D4AF37]">{err}</div>}
          <button data-testid="login-submit" type="submit" disabled={busy} className="btn-ghost w-full justify-center mt-2">
            {busy ? "Signing in…" : "Sign In with Email"}
          </button>
        </form>

        <div className="mt-6 text-sm text-muted-foreground">
          New here? <Link to="/register" className="text-neon-cyan" data-testid="login-to-register">Create an account</Link>
        </div>
      </div>
    </div>
    </>
  );
}
